import { useEffect, useState, type ReactNode } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import { onAuthStateChanged, type User } from 'firebase/auth';

import { auth } from './firebase';
import AppShell from './components/AppShell';
import Feed from './components/Feed';
import Friends from './components/Friends';
import Login from './components/Login';
import PageTitle from './components/PageTitle';
import PlayerPuns from './components/PlayerPuns';
import Rules from './components/Rules';
import Settings from './components/Settings';
import SharedPun from './components/SharedPun';
import Signup from './components/Signup';
import Today from './components/Today';
import You from './components/You';

/**
 * Renders its children only for a signed-in user, otherwise redirects to login.
 *
 * `children` is a function rather than an element so the non-null user can be
 * handed to it, which puts the guarantee in the types instead of making every
 * route assert `user!`.
 */
function ProtectedRoute({
  children,
  user,
}: {
  children: (user: User) => ReactNode;
  user: User | null;
}) {
  if (!user) return <Navigate to="/" replace />;
  return children(user);
}

function Page({ title, children }: { title: string; children: ReactNode }) {
  return (
    <>
      <PageTitle title={title} />
      {children}
    </>
  );
}

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(
    () =>
      onAuthStateChanged(auth, (signedIn) => {
        setUser(signedIn);
        setLoading(false);
      }),
    [],
  );

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper">
        <span className="h-10 w-10 animate-spin rounded-full border-[3px] border-ink/15 border-t-ink" />
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route
          path="/"
          element={user ? <Navigate to="/today" replace /> : <Page title="Sign in"><Login /></Page>}
        />
        <Route
          path="/signup"
          element={user ? <Navigate to="/today" replace /> : <Page title="Sign up"><Signup /></Page>}
        />

        {/*
          A shared pun, open to anyone.

          Deliberately outside the auth gate and above the signed-in routes: the
          entire point is that someone with no account can follow a link from a
          group chat and read the pun. Signing them in first would show them a
          login form instead of the thing they were sent.
        */}
        <Route path="/p/:punId" element={<SharedPun />} />

        {/* Everything signed-in shares one chrome: masthead, outlet, tab bar. */}
        <Route
          element={
            <ProtectedRoute user={user}>{(signedIn) => <AppShell user={signedIn} />}</ProtectedRoute>
          }
        >
          <Route path="/today" element={<Page title="Today"><Today /></Page>} />
          <Route path="/feed" element={<Page title="Feed"><Feed /></Page>} />
          <Route path="/you" element={<Page title="You"><You /></Page>} />
          <Route path="/friends" element={<Page title="Friends"><Friends /></Page>} />
          <Route path="/settings" element={<Page title="Settings"><Settings /></Page>} />
          <Route path="/rules" element={<Page title="How it works"><Rules /></Page>} />
          <Route path="/user/:userId" element={<Page title="Player"><PlayerPuns /></Page>} />
          {/* Kept because friend links in the wild point at it. */}
          <Route path="/user/:userId/puns" element={<Page title="Player"><PlayerPuns /></Page>} />
        </Route>

        {/*
          The old URLs. Notification deep links and anything a player has
          bookmarked still land somewhere sensible rather than on a blank page.
        */}
        <Route path="/dashboard" element={<Navigate to="/today" replace />} />
        <Route path="/my-puns" element={<Navigate to="/you" replace />} />
        <Route path="/profile-settings" element={<Navigate to="/settings" replace />} />
        <Route path="/how-it-works" element={<Navigate to="/rules" replace />} />

        <Route path="*" element={<Navigate to={user ? '/today' : '/'} replace />} />
      </Routes>
    </Router>
  );
}

export default App;
